import React, { useEffect, useMemo } from "react";
import {
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	Button,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TextField,
	InputAdornment,
} from "@mui/material";
import { useGetMySurveys, useGetSurveysByUserIdQuery } from "@onair/operations/queries/survey";
import { SurveyDetailsDTO } from "@onair/repositories";
import { TableHeadCustom } from "@onair/minimal-ui/table";
import { FORMAT_DATE_TIME_CLEANER } from "@onair/utils/dayjs";
import dayjs from "dayjs";
import { Iconify } from "@onair/minimal-ui/iconify";
import { htmlToText } from "@onair/utils/html-to-text";
import TextMaxLine from "#src/components/text-max-line";

interface SelectSurveyProps {
	selectedIds?: string[];
	open: boolean;
	onClose: () => void;
	onUpdate: (selectedIds: string[]) => void;
	userId?: string;
}

export const SurveyPicker: React.FC<SelectSurveyProps> = (props) => {
	const { open, onClose, onUpdate, selectedIds, userId } = props;

	const [selectedSurveys, setSelectedSurveys] = React.useState<string[]>(
		selectedIds || [],
	);
	const [searchTerm, setSearchTerm] = React.useState<string>("");

	const { data: mySurveysData } = useGetMySurveys(
		{
			limit: Number.MAX_SAFE_INTEGER,
			offset: 1,
			search: searchTerm,
		},
		{
			enabled: open && !userId,
		},
	);

	const { data: userSurveysData } = useGetSurveysByUserIdQuery(
		{
			userId: userId || "",
			limit: Number.MAX_SAFE_INTEGER,
			offset: 1,
			search: searchTerm,
		},
		{
			enabled: open && !!userId,
		},
	);

	const handleClose = () => {
		setSelectedSurveys([]);
		onClose();
	};

	const handleConfirm = () => {
		onUpdate(selectedSurveys);
		onClose();
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const handleAddSurvey = (surveyId: string) => {
		if (!selectedSurveys.includes(surveyId)) {
			setSelectedSurveys((prev) => [...prev, surveyId]);
		}
	};

	const handleRemoveSurvey = (surveyId: string) => {
		setSelectedSurveys((prev) => prev.filter((id) => id !== surveyId));
	};

	const selectedChecker = useMemo(() => {
		return new Set(selectedSurveys);
	}, [selectedSurveys]);

	useEffect(() => {
		setSelectedSurveys(selectedIds || []);
	}, [selectedIds]);

	const items: SurveyDetailsDTO[] = userId
		? userSurveysData?.items || []
		: mySurveysData?.items || [];

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			scroll="paper"
		>
			<DialogTitle>Thư viện khảo sát</DialogTitle>
			<DialogContent>
				<TextField
					size="small"
					placeholder="Tìm kiếm"
					value={searchTerm}
					onChange={handleSearchChange}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Iconify icon="eva:search-fill" />
							</InputAdornment>
						),
					}}
				/>

				<Table className="mt-4">
					<TableHeadCustom headLabel={TABLE_HEAD} />
					<TableBody>
						{items.map((item, index) => (
							<TableRow key={item.id}>
								<TableCell>{index + 1}</TableCell>
								<TableCell>{item.title}</TableCell>
								<TableCell>
									<TextMaxLine variant="body2" line={2}>
										{item.description ? htmlToText(item.description) : "--"}
									</TextMaxLine>
								</TableCell>
								<TableCell>
									{dayjs(item.createdAt).format(FORMAT_DATE_TIME_CLEANER)}
								</TableCell>
								<TableCell>
									{selectedChecker.has(item.id) ? (
										<Button
											variant="contained"
											color="primary"
											size="small"
											onClick={() => {
												handleRemoveSurvey(item.id);
											}}
										>
											Đã chọn
										</Button>
									) : (
										<Button
											variant="outlined"
											color="primary"
											size="small"
											onClick={() => {
												handleAddSurvey(item.id);
											}}
										>
											Chọn
										</Button>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose}>
					Đóng
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={handleConfirm}
					autoFocus
				>
					Thêm khảo sát
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const TABLE_HEAD = [
	{ id: "stt", label: "STT", width: "5%" },
	{ id: "title", label: "Tiêu đề", width: "30%" },
	{ id: "description", label: "Mô tả", width: "30%" },
	{ id: "createAt", label: "Ngày tạo", width: "20%" },
	{ id: "actions", label: "Hành động", width: "15%" },
];
